import React, { useState } from "react";
import { SubTitle } from "../ReviewBoard/ReviewBoardStyles";
import {
  Board,
  BoardContext,
  boardHasBeenPaidFor,
  QuoteState,
} from "../../../reducers/uploadedBoard";
import { REQUIRED } from "../../../constants/Text";
import { useProjectName } from "../../../utils/hooks/commonly-stored/uploaded-board/projectName";
import { FieldTypes } from "../../formik-form-components/FormikFormLine/constants";
import { useBoardFromContext } from "../../../utils/hooks/commonly-stored/uploaded-board/board";
import ShareBoard from "../../ShareBoard";
import { useAppSelector } from "../../../utils/hooks/store";
import ProjectOwner from "../../ShareBoard/ProjectOwner";
import { FakeLink, Input, InputLabel, Pill } from "../../../constants/Styles";
import Button from "../../common/Button";
import QuoteDetails from "./QuoteDetails";
import LineItem from "../../common/LineItem";
import { formatCurrency } from "../../../utils/valueFormatting";
import { SHOPPING_CART } from "../../../constants/routes";
import { useRouter } from "next/router";
import {
  Container,
  DetailsCard,
  DetailsTopLine,
  EditableNickname,
  EditButton,
  ParentContainer,
  ProjectName,
  QuoteWarningText,
} from "./ProjectDetailsStyles";
import PhysicalDetailsForm from "./PhysicalDetails";
import { STRIPE_MINIMUM_PAYMENT } from "../../../constants/orders";
import ProjectDetailsCallToAction from "./ProjectDetailsCallToAction";
import { ADMIN_GROUP, ENGINEER_GROUP } from "../../../utils/group";
import { ProjectDetailsBoardAlerts } from "../BoardAlerts";
import Link from "next/link";
import ModalContentWithCloseButton from "../../common/ModalContentWithCloseButton";
import Modal from "../../common/Modal";
import UpdateBoardNickName from "../../ManufacturersPanel/Dashboard/ShipmentCard/UpdateBoardNickName";
import { useUpdateBoardNickname } from "../../../utils/hooks/commonly-stored/uploaded-board/updates/board";
import Icon from "../../common/Icon";

export function ProcessingPill() {
  return (
    <div style={{ padding: "0.5rem 0" }}>
      <Pill width="100px" />
    </div>
  );
}

const ProjectDetails = () => {
  const router = useRouter();
  const [shareBoardId, setShareBoardId] = useState<string | null>(null);
  const [updateNickname, setUpdateNickname] = useState<boolean>(false);
  const { updatingNickname } = useUpdateBoardNickname();
  const { userId, groups } = useAppSelector((state) => state.auth);
  const board = useBoardFromContext().board as Board;
  const { id: boardId } = board;
  const { bindProjectNameCache } = useProjectName();
  const isProcessingQuote =
    useAppSelector(
      (state) =>
        (state.uploadedBoard.boards[boardId] as BoardContext).isProcessing,
    ) && board.quote.state != QuoteState.ORDER_RECEIVED;
  const isAdminOrEngineer =
    groups.includes(ADMIN_GROUP) || groups.includes(ENGINEER_GROUP);

  const closeModal = () => {
    setUpdateNickname(false);
  };
  const quoteIsUnset =
    board.quote.total === null && board.quote.estimated_total === null;
  return (
    <>
      <ShareBoard
        shareBoardId={shareBoardId}
        setShareBoardId={setShareBoardId}
      />
      <ParentContainer>
        <DetailsCard>
          <DetailsTopLine>
            <SubTitle>Project Details</SubTitle>
            {((board.user_id !== null && board.user_id === userId) ||
              isAdminOrEngineer) && (
              <Button
                isSmallSize
                onClick={() => setShareBoardId(boardId)}
                label={"Share"}
              />
            )}
          </DetailsTopLine>
          {board.user_id != userId && (
            <ProjectOwner boardId={board.id} userId={board.user_id} />
          )}
          <Container>
            <InputLabel htmlFor="board-preview-project-name">
              Project name:
            </InputLabel>
            {boardHasBeenPaidFor(board) ? (
              <ProjectName>{board.project_name}</ProjectName>
            ) : (
              <Input
                id="board-preview-project-name"
                type={FieldTypes.text}
                placeholder={REQUIRED}
                name="project name"
                {...bindProjectNameCache}
              />
            )}
          </Container>
          {isAdminOrEngineer && (
            <Container>
              <InputLabel>Internal nickname:</InputLabel>
              <EditableNickname>
                <ProjectName>{board.internal_nickname}</ProjectName>
                <EditButton
                  onClick={() => setUpdateNickname(true)}
                  disabled={updatingNickname}
                >
                  <Icon src="/icons/pencil.svg" alt="edit" />
                </EditButton>
              </EditableNickname>
            </Container>
          )}
          <PhysicalDetailsForm />
          <QuoteDetails />
          {quoteIsUnset ? (
            <>
              <LineItem leftText="Price per board" isUpdating />
              <LineItem leftText="Total" isUpdating />
            </>
          ) : (
            <>
              {board.quote.total_per_board !== null &&
                board.quote.total_per_board > 0 && (
                  <LineItem
                    leftText="Price per board"
                    rightText={formatCurrency(
                      board.quote.total_per_board,
                      board.quote.currency,
                    )}
                    isUpdating={isProcessingQuote}
                  />
                )}
              {board.quote.total !== null &&
                board.quote.sub_total !== null &&
                board.quote.sub_total > 0 && (
                  <LineItem
                    leftText="Total"
                    rightText={formatCurrency(
                      board.quote.sub_total > STRIPE_MINIMUM_PAYMENT
                        ? board.quote.sub_total
                        : 0,
                      board.quote.currency,
                    )}
                    isUpdating={isProcessingQuote}
                  />
                )}
              {board.quote.estimated_total !== null &&
                board.quote.sub_total !== null &&
                board.quote.sub_total > 0 && (
                  <LineItem
                    leftText="Estimated total"
                    rightText={formatCurrency(
                      board.quote.sub_total > STRIPE_MINIMUM_PAYMENT
                        ? board.quote.sub_total
                        : 0,
                      board.quote.currency,
                    )}
                    isUpdating={isProcessingQuote}
                  />
                )}
            </>
          )}
          {board.quote.tariff !== null && board.quote.tariff > 0 && (
            <LineItem
              leftText="Tariff"
              rightText={formatCurrency(
                board.quote.tariff,
                board.quote.currency,
              )}
              isUpdating={isProcessingQuote || quoteIsUnset}
            />
          )}
          {board.order_summary !== null && (
            <LineItem
              leftText="Order"
              rightText={
                <Link
                  href={`/orders/${board.order_summary.id}`}
                  target={"_blank"}
                >
                  {board.order_summary.id}
                </Link>
              }
            />
          )}
        </DetailsCard>
        <ProjectDetailsBoardAlerts
          board={board}
          isUpdating={isProcessingQuote}
        />
        <ProjectDetailsCallToAction />
        <FakeLink
          style={{ textDecoration: "none" }}
          onClick={() => void router.push(SHOPPING_CART)}
        >
          My shopping cart
        </FakeLink>
        {isProcessingQuote && (
          <QuoteWarningText>
            Please wait for quote to finish updating
          </QuoteWarningText>
        )}
      </ParentContainer>
      <Modal isOpen={updateNickname} onRequestClose={closeModal}>
        <ModalContentWithCloseButton closeModal={closeModal}>
          <UpdateBoardNickName board={board} closeModal={closeModal} />
        </ModalContentWithCloseButton>
      </Modal>
    </>
  );
};

export default ProjectDetails;
